import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  CountriesServiceV2,
  EstablishmentV2,
  NotificationsServiceV2,
  NotificationV2,
  PaymentLinkV2,
} from 'api-client';
import { Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { LogService } from './log.service';
import { CallbackMessage, Translations, Transaction } from '../../payment/models';

@Injectable({
  providedIn: 'root',
})
export class SharedFunctionsService {
  private _counter = 0;
  private _translations: Translations = new Translations();

  private _httpClient = inject(HttpClient);
  private _translate = inject(TranslateService);
  private _logger = inject(LogService);
  private _notificationsService = inject(NotificationsServiceV2);
  private _countriesService = inject(CountriesServiceV2);

  convertBrasiliaDatetimeToUTC(datetime: string) {
    if (datetime && datetime !== '') {
      try {
        return new Date(datetime.replace('T', ' ').replace('Z', '') + ' GMT-3:00').toISOString();
      } catch {
        return new Date().toISOString();
      }
    } else {
      return new Date().toISOString();
    }
  }

  decrementCounter() {
    this._counter--;
  }

  generateRandomLetter() {
    const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

    return alphabet[Math.floor(Math.random() * alphabet.length)];
  }

  getCounter() {
    return this._counter;
  }

  getTranslation(key: string) {
    return this._translations[key];
  }

  getTranslations() {
    return this._translations;
  }

  incrementCounter() {
    this._counter++;
  }

  initTranslations(countryCode: string): Observable<Translations> {
    this._translate.use(countryCode);

    return this._translate
      .get([
        'emailCustomer.hello',
        'emailCustomer.confirmationOrder',
        'emailCustomer.doneInEstablishment',
        'emailCustomer.deliveryProduct',
        'emailCustomer.voucherAttachment',
        'emailCustomer.thankYouForChoosingDreampay',

        'emailObs.greetingFromTeam',
        'emailObs.importantObs',
        'emailObs.emailAutomatic',
        'emailObs.inCaseNoVoucher',
        'emailObs.inCaseNoPurchase',
        'emailObs.emailSubject',

        'emailEstablishment.forEstablishment',
        'emailEstablishment.yourClientPurchased',
        'emailEstablishment.giveProductsToCustomer',
        'emailEstablishment.voucherAttached',
        'emailEstablishment.experienceSeeYouSoon',
      ])
      .pipe(
        map((translations) => {
          this._translations.eCHello = translations['emailCustomer.hello'];
          this._translations.eCConfirmationOrder = translations['emailCustomer.confirmationOrder'];
          this._translations.eCdoneInEstablishment =
            translations['emailCustomer.doneInEstablishment'];
          this._translations.eCdeliveryProduct = translations['emailCustomer.deliveryProduct'];
          this._translations.eCvoucherAttachment = translations['emailCustomer.voucherAttachment'];
          this._translations.eCthankYouForChoosingDreampay =
            translations['emailCustomer.thankYouForChoosingDreampay'];

          this._translations.eOgreetings = translations['emailObs.greetingFromTeam'];
          this._translations.eOimportantObs = translations['emailObs.importantObs'];
          this._translations.eOemailAutomatic = translations['emailObs.emailAutomatic'];
          this._translations.eOinCaseNoVoucher = translations['emailObs.inCaseNoVoucher'];
          this._translations.eOinCaseNoPurchase = translations['emailObs.inCaseNoPurchase'];
          this._translations.emailSubjectString = translations['emailObs.emailSubject'];

          this._translations.eEforEstablishment =
            translations['emailEstablishment.forEstablishment'];
          this._translations.eEyourClientPurchased =
            translations['emailEstablishment.yourClientPurchased'];
          this._translations.eEgiveProductsToCustomer =
            translations['emailEstablishment.giveProductsToCustomer'];
          this._translations.eEvoucherAttached = translations['emailEstablishment.voucherAttached'];
          this._translations.eEexperienceSeeYouSoon =
            translations['emailEstablishment.experienceSeeYouSoon'];
          return this._translations;
        }),
        catchError((error) => {
          this._logger.logToSentry('initTranslations error: ', error);
          throw error;
        }),
      );
  }

  obfuscateCreditCardNumber(cardNumber: string) {
    return (
      cardNumber.substring(0, 7) +
      '*'.repeat(cardNumber.length - 10) +
      cardNumber.substring(cardNumber.length - 5)
    );
  }

  postToCallbackUrl(callBackUrl: string, callbackMessage: CallbackMessage) {
    const httpOptions = {};

    this._httpClient
      .post<unknown>(callBackUrl, callbackMessage, httpOptions)
      .pipe(take(1))
      .subscribe((response) => {
        this._logger.logToSentry('postToCallbackUrl response: ', response);
      });
  }

  async getUiLanguageCode(countryId: number): Promise<string> {
    try {
      const res = await this._countriesService.apiV2CountriesIdGet(countryId).toPromise();
      return res?.uiLanguage?.trim().toLowerCase() ?? 'pt';
    } catch (err) {
      this._logger.error(
        'Failed to get UI language code',
        'SharedFunctionsService.getUiLanguageCode',
        err,
      );
      return 'pt';
    }
  }

  async sendAlertOpened(
    paymentLink: PaymentLinkV2,
    establishment: EstablishmentV2,
    currSymbol: string,
  ) {
    if (paymentLink.customerName !== '') {
      const uiLanguage = await this.getUiLanguageCode(establishment.countryId!);
      const notification: NotificationV2 = {
        emailAddress: establishment.emailAddress!,
        cc: '',
        subject: uiLanguage === 'en' ? 'Payment Link Opened' : 'Link de pagamento aberto',
        body:
          uiLanguage === 'en'
            ? 'Hello, ' +
              establishment.establismentName +
              '<br/>' +
              'The customer ' +
              (paymentLink.customerName || '?') +
              ' with the order number ' +
              paymentLink.orderNumber +
              ' of ' +
              (paymentLink.value?.toString() || '?') +
              currSymbol +
              ' has opened your payment link: ' +
              "<br/>Link: <a href='https://" +
              paymentLink.link +
              "' target='_blank'>https://" +
              paymentLink.link +
              '</a>' +
              "<br/><img src='" +
              paymentLink.qrcodeUrl +
              "' alt='qr-code' style='width: 250px; height:250px'>"
            : 'Olá, ' +
              establishment.establismentName +
              '<br/>' +
              'O cliente ' +
              paymentLink.customerName +
              ' com o nº de encomenda ' +
              paymentLink.orderNumber +
              ' de ' +
              (paymentLink.value?.toString() || '?') +
              currSymbol +
              ' abriu o seu link de pagamento: ' +
              "<br/>Link: <a href='https://" +
              paymentLink.link +
              "' target='_blank'>https://" +
              paymentLink.link +
              '</a>' +
              "<br/><img src='" +
              paymentLink.qrcodeUrl +
              "' alt='qr-code' style='width: 250px; height:250px'>",
        emailTitle: uiLanguage === 'en' ? 'Payment Link Opened' : 'Link de pagamento aberto',
        imageToUse: 'order-place.png',
        language: uiLanguage,
      };

      this._notificationsService
        .apiV2NotificationsPost(notification)
        .pipe(take(1))
        .subscribe(
          (_result) => {
            this._logger.debug('Notification sent successfully');
          },
          (error) => {
            this._logger.error(error);
          },
        );
    }
  }

  async sendAlertOpenedCheckout(
    paymentLink: PaymentLinkV2,
    establishment: EstablishmentV2,
    currSymbol: string,
    customerName: string,
    customerCPF: string,
    customerEmail: string,
    customerPhoneNumber: string,
    value: number,
  ) {
    if (customerName !== '') {
      const uiLanguage = await this.getUiLanguageCode(establishment.countryId!);
      const notification: NotificationV2 = {
        emailAddress: establishment.emailAddress!,
        cc: '',
        subject: uiLanguage === 'en' ? 'Payment Link Opened' : 'Link de pagamento aberto',
        body:
          uiLanguage === 'en'
            ? 'Hello, ' +
              establishment.establismentName +
              '<br/>' +
              ' The client ' +
              customerName +
              ' with the cpf: ' +
              customerCPF +
              ', email: ' +
              customerEmail +
              ' and phone number: ' +
              customerPhoneNumber +
              ' has opened your payment link: ' +
              "<br/>Link: <a href='https://" +
              paymentLink.link +
              "' target='_blank'>https://" +
              paymentLink.link +
              '</a>' +
              "<br/><img src='" +
              paymentLink.qrcodeUrl +
              "' alt='qr-code' style='width: 250px; height:250px'>" +
              ' with the value of ' +
              value.toString() +
              currSymbol
            : 'Olá, ' +
              establishment.establismentName +
              '<br/>' +
              'O cliente ' +
              customerName +
              ' com o cpf: ' +
              customerCPF +
              ', email: ' +
              customerEmail +
              ' e número de telemóvel: ' +
              customerPhoneNumber +
              ' abriu o seu link de checkout de pagamento: ' +
              "<br/>Link: <a href='https://" +
              paymentLink.link +
              "' target='_blank'>https://" +
              paymentLink.link +
              '</a>' +
              "<br/><img src='" +
              paymentLink.qrcodeUrl +
              "' alt='qr-code' style='width: 250px; height:250px'>" +
              ' com o valor de ' +
              value.toString() +
              currSymbol,
        emailTitle: uiLanguage === 'en' ? 'Payment Link Opened' : 'Link de pagamento aberto',
        imageToUse: 'order-place.png',
        language: uiLanguage,
      };

      this._notificationsService
        .apiV2NotificationsPost(notification)
        .pipe(take(1))
        .subscribe(
          (_result) => {
            this._logger.debug('Notification sent successfully');
          },
          (error) => {
            this._logger.error(error);
          },
        );
    }
  }

  async sendFailedPayEmail(
    transaction: Transaction,
    establishment: EstablishmentV2,
    paymentLink: PaymentLinkV2,
  ) {
    const uiLanguage = await this.getUiLanguageCode(establishment.countryId!);
    const notification: NotificationV2 = {
      emailAddress: establishment.emailAddress!,
      cc: '',
      subject: uiLanguage === 'en' ? 'Failed payment From Link' : 'Falha no pagamento de Link',
      body:
        uiLanguage === 'en'
          ? 'Hello, ' +
            transaction.establismentName +
            '<br/>' +
            'The client ' +
            (paymentLink.customerName || '?') +
            ' with the order number ' +
            (paymentLink.orderNumber || '?') +
            ' of ' +
            transaction.purchaseValue +
            transaction.currencyCode +
            ' has a failed payment with the link: ' +
            "<br/>Link: <a href='" +
            paymentLink.link +
            "' target='_blank'>" +
            paymentLink.link +
            '</a>' +
            "<br/><img src='" +
            paymentLink.qrcodeUrl +
            "' alt='qr-code' style='width: 250px; height:250px'>" +
            '<br/>' +
            ' Here is some information of the failed transaction: ' +
            '<br/>' +
            'Installments: ' +
            transaction.installments +
            '<br/>' +
            'Installment Value: ' +
            transaction.installmentValueBRL +
            '$R' +
            '<br/>' +
            'Payment ID: ' +
            transaction.paymentID +
            '<br/>' +
            'Card brand: ' +
            transaction.cardType +
            '<br/>'
          : 'Olá, ' +
            transaction.establismentName +
            '<br/>' +
            'O cliente ' +
            paymentLink.customerName +
            ' com o nº de encomenda ' +
            paymentLink.orderNumber +
            ' de ' +
            transaction.purchaseValue +
            transaction.currencyCode +
            ' tem uma tentativa de pagamento falhada com o link: ' +
            "<br/>Link: <a href='" +
            paymentLink.link +
            "' target='_blank'>" +
            paymentLink.link +
            '</a>' +
            "<br/><img src='" +
            paymentLink.qrcodeUrl +
            "' alt='qr-code' style='width: 250px; height:250px'>" +
            '<br/>' +
            ' Aqui estão algumas informações da transação falhada: ' +
            '<br/>' +
            'Parcelas: ' +
            transaction.installments +
            '<br/>' +
            'Valor parcela: ' +
            transaction.installmentValueBRL +
            '$R' +
            '<br/>' +
            'ID Pagamento: ' +
            transaction.paymentID +
            '<br/>' +
            'Tipo Cartão: ' +
            transaction.cardType +
            '<br/>',
      emailTitle: uiLanguage === 'en' ? 'Failed payment From Link' : 'Falha no pagamento de Link',
      imageToUse: 'notification-warning.png',
      language: uiLanguage,
    };

    this._notificationsService
      .apiV2NotificationsPost(notification)
      .pipe(take(1))
      .subscribe(
        (result) => {
          this._logger.debug(result);
        },
        (error) => {
          this._logger.error(error);
        },
      );
  }
}
